import { EVIDENCE_WEIGHT, type EvidenceKind, type MemoryStatus } from "./types";
import { DAY_MS } from "./window";
import { screenRecalledMemory, type ClaimScreen } from "./memorySaveGate";

/**
 * Agent Brains section 5.1's own charter (`apps/web/src/lib/agents.ts`'s
 * `research` entry), verbatim: mission "Build the lead card before anyone
 * talks to the lead."; never "Never customer-facing. Never states an
 * inference as a fact."; handoff "Feeds Conversations and Routing.";
 * produces "A lead card with confidence scores and cited sources."
 *
 * THE LITERAL CHARTER STAYS BLOCKED, EXACTLY AS BEFORE THIS FILE. "Build the
 * lead card before anyone talks to the lead" describes pre-first-contact
 * profile enrichment: no lead source exists anywhere in this codebase, and
 * reading a customer's own platform profile needs Meta App Review, which is
 * unapproved. Nothing here scrapes anything, invents a lead source, or
 * stands in for one. `apps/web/src/lib/agents.ts`'s `blockedBy` for
 * `research` still names that half as blocked, because it still is.
 *
 * WHAT IS ACTUALLY BUILDABLE, and what this file is: the charter's own
 * `produces` line, read plainly, does not require external enrichment. "A
 * lead card with confidence scores and cited sources" is exactly what
 * CLAUDE.md sections 4 and 6 already build, one message at a time, and never
 * assembled into one artifact: `EVIDENCE_WEIGHT` (`types.ts`) already prices
 * a fact deterministically by the observation that produced it, never by a
 * model's own self-graded certainty; `contact_memory` (`packages/db/src/
 * schema.ts`) already stores curated per-contact extractions carrying a
 * written-at date, an evidence kind, a fact/inference flag, a confidence
 * (always null for a fact, B2) and the save-gate's own logged reason; and
 * `memory_status` already has a `held` value for section 4's contradiction
 * hold ("two sources disagree on a fact, the fact is held entirely and
 * surfaced to a human"), proven reachable by `schema.test.ts`'s own
 * "contradiction-hold status" test but, before this file, read by nothing:
 * `composeHandler.ts`'s `loadContext` and `memorySummary.ts` both query and
 * render `status = 'active'` only, on purpose, because a held fact must
 * never ground a customer-facing reply. See FINDINGS.md TA-186 for the full
 * account of the grep that confirmed no such assembly existed anywhere in
 * `packages/core` or `packages/agents` before this task.
 *
 * `buildLeadCard` is that assembly: a pure, read-only function over what
 * Memory already knows about ONE contact. It invents no fact, no inference
 * and no held contradiction; every row it renders already exists as a
 * `contact_memory` row written by the save-gate, which this task does not
 * touch (no new write path, per the brief). What this file adds is the
 * shape: three arrays a caller cannot confuse with one another (see the type
 * comments below), where today Memory's own rows are just `content` plus an
 * `isInference` boolean.
 *
 * NEVER CUSTOMER-FACING (the charter's own `never` line, and section 6's
 * memory-quarantine rule). A `LeadCard` is built for an operator or another
 * internal agent (Routing, Conversations) reading what the shop knows about
 * someone, never for that someone. This module, and the DB loader around it
 * in `packages/agents/src/leadCard.ts`, are not imported by `replyAgent.ts`,
 * `composeHandler.ts`, or any other call site that ends in text a customer
 * reads (grep this repo for `leadCard` before wiring one in, and see
 * FINDINGS.md TA-186 for why that wiring was deliberately left undone here).
 * If a future change ever surfaces a lead card's content to a customer, it
 * must first pass every field back through quarantine exactly as
 * `memorySummary.ts`'s rendered lines already do: a `LeadCardFact`'s
 * `sourceQuote` is a customer's own verbatim words, which is precisely the
 * stored-and-replayed attack surface section 6 exists to contain.
 */

/**
 * B2's own recency half-life, duplicated from `MEMORY_HALF_LIFE_DAYS`
 * (`packages/agents/src/memorySummary.ts`) rather than imported, because
 * `packages/core` cannot depend on `packages/agents` (the dependency runs
 * the other way; `retroGrill.ts`'s `RETRO_GRILL_WIN_MIN_SAMPLE` duplicates
 * `playcard.ts`'s `PATTERN_MIN_SAMPLE` for the identical reason). The two
 * numbers must move together if either is ever ratified to something other
 * than 180: a lead card and a compose-time memory summary silently
 * disagreeing about how fast a fact goes stale would be its own defect.
 * AWAITING RATIFICATION, same as the original.
 */
export const LEAD_CARD_MEMORY_HALF_LIFE_DAYS = 180;

/** The `memory_status` values, as `MemoryStatus` in `types.ts` declares them
 * once. This used to be a second hand-typed copy of the union, justified by
 * the enum living in `packages/db`; the tuple now lives in core and the
 * pgEnum is built from it (core-duplication/memory-status-union-declared-twice).
 * Kept as an alias so existing importers compile unchanged. */
export type LeadCardMemoryStatus = MemoryStatus;

/**
 * One `contact_memory` row, in the shape this file needs. Deliberately not
 * the DB row itself: `packages/agents/src/leadCard.ts` is the only place
 * that reads the real table and maps it to this shape, the same split
 * `composeHandler.ts`'s `loadContext` draws against `memorySummary.ts`'s
 * `MemoryEntry`.
 */
export interface LeadCardMemoryRow {
  readonly memoryId: string;
  readonly content: string;
  /** Facts get evidence pricing; only inferences carry model confidence (B2). */
  readonly isInference: boolean;
  /** A string, not `EvidenceKind`, because this is the boundary from data
   * that has not yet been validated. An unrecognised kind is excluded and
   * counted (see `buildLeadCard`), never guessed at (section 0). */
  readonly evidenceKind: string;
  /** Null for a clean fact, by design (B2: a model never self-grades a
   * fact's certainty). Required, non-null for a clean inference. Either
   * combination violated is a malformed row (see `buildLeadCard`). */
  readonly confidence: number | null;
  /** Verbatim source, so a stored fact or inference can always be traced
   * back to what was actually said. Null is not "no source": the citation
   * is always at minimum `evidenceKind`, `writtenAt` and `memoryId`. */
  readonly sourceQuote: string | null;
  /** The save-gate's own logged reason (B2: "an explicit, logged decision
   * with a reason"). For a held row, this is the human-readable account of
   * the contradiction the schema itself does not otherwise structure: the
   * schema has no separate field linking a held row to the row it
   * conflicts with (`schema.test.ts`'s own worked example puts "conflicts
   * with earlier 8k" in this very field), so this is the one place that
   * account lives, and this file surfaces it rather than inventing a
   * two-sided structure the schema does not have (FINDINGS.md TA-186). */
  readonly saveReason: string;
  readonly status: LeadCardMemoryStatus;
  /** ISO timestamp. */
  readonly writtenAt: string;
}

export interface BuildLeadCardInput {
  readonly contactId: string;
  readonly memory: readonly LeadCardMemoryRow[];
  readonly now: Date;
  /**
   * M1, the vault's fragmentation defense: the rows this card aggregates are
   * screened again as one text, with the same screen the save-gate uses
   * (`memoryClaimScreen` in packages/gateway; core cannot import the
   * gateway's). Required, so a caller cannot forget it and get a card that
   * quietly skipped the check.
   */
  readonly screen: ClaimScreen;
}

/**
 * A deterministically-priced fact (section 4: "the model never self-grades
 * a fact's confidence... facts get deterministic evidence-kind pricing").
 * Structurally distinct from `LeadCardInference`: THIS TYPE HAS NO
 * `confidence` FIELD AT ALL. A caller cannot read a fact's calibrated
 * certainty because there isn't one, by construction, not by convention.
 */
export interface LeadCardFact {
  readonly content: string;
  readonly evidenceKind: EvidenceKind;
  /**
   * `EVIDENCE_WEIGHT[evidenceKind]`, decayed by age against
   * `LEAD_CARD_MEMORY_HALF_LIFE_DAYS`. NOT a probability and not a
   * confidence score: a fact is either held (see `LeadCardHeldContradiction`)
   * or it is true, section 4 has no third state. This is a display and
   * ranking number only, so a reader (or a future sort) can see that a fact
   * from eight months ago is less current than the same evidence kind
   * stated yesterday, without the fact ever being excluded for its age
   * (FINDINGS.md TA-186 logs the reasoning for decay-not-exclusion).
   */
  readonly weight: number;
  readonly writtenAt: string;
  readonly sourceQuote: string | null;
  readonly saveReason: string;
  readonly memoryId: string;
}

/**
 * A calibrated behavioural inference (section 4: "calibrated confidence
 * applies to behavioural inferences only"). Structurally distinct from
 * `LeadCardFact`: `confidence` is REQUIRED and non-null here. An inference
 * with nothing to calibrate cannot be represented by this type at all; see
 * `buildLeadCard`'s handling of a row that claims to be an inference but
 * carries a null confidence, which is excluded rather than defaulted.
 */
export interface LeadCardInference {
  readonly content: string;
  readonly evidenceKind: EvidenceKind;
  readonly confidence: number;
  /** Same decay as a fact's weight, further scaled by `confidence`, so a
   * 0.2 hunch drawn from strong evidence still ranks below a fact drawn
   * from the same evidence (mirrors `memorySummary.ts`'s `score`). */
  readonly weight: number;
  readonly writtenAt: string;
  readonly sourceQuote: string | null;
  readonly saveReason: string;
  readonly memoryId: string;
}

/**
 * Section 4's contradiction hold, surfaced as its own third thing, never
 * folded into `facts` or `inferences`. NO `weight` AND NO `confidence` FIELD
 * EXISTS ON THIS TYPE: an unresolved fact is never 60% true, and this file
 * does not compute a number that would make it look like one. A reader sees
 * the held content and the save-gate's own reason for holding it; nothing
 * here stands in for a human resolving it.
 */
export interface LeadCardHeldContradiction {
  readonly content: string;
  readonly evidenceKind: EvidenceKind;
  readonly isInference: boolean;
  readonly writtenAt: string;
  readonly saveReason: string;
  readonly memoryId: string;
}

/**
 * M1. A row whose content, combined with the others on this card, the memory
 * screen flags. Excluded from every list above and named here so it is never
 * silently vanished. NO `content` FIELD: the card names the row, it does not
 * replay it. The operator reads it by `memoryId`.
 */
export interface LeadCardQuarantined {
  readonly memoryId: string;
  readonly evidenceKind: string;
  readonly writtenAt: string;
  /** The screen's categories for the combination, never its text. */
  readonly categories: readonly string[];
}

export interface LeadCard {
  readonly contactId: string;
  /** `now.toISOString()`, so a card is stamped with when it was assembled,
   * never confused with when any fact on it was written. */
  readonly generatedAt: string;
  /** Ranked highest weight first (see `LeadCardFact.weight`). */
  readonly facts: readonly LeadCardFact[];
  /** Ranked highest weight first. */
  readonly inferences: readonly LeadCardInference[];
  /** Ranked most recent first. Never ranked by weight: this file computes
   * no weight for a held row (see `LeadCardHeldContradiction`). */
  readonly heldContradictions: readonly LeadCardHeldContradiction[];
  /** Every row this card was handed, retired rows included. Never capped
   * (the `outcomeDigest.ts` discipline: the true total is never hidden). */
  readonly totalRows: number;
  /** Rows excluded because their status is `superseded` or `pruned`:
   * retired knowledge, not current knowledge, and not decayed knowledge
   * either. Counted, never silently vanished. */
  readonly excludedRetired: number;
  /**
   * Rows excluded because they blur fact and inference at the source, or
   * carry an evidence kind this repo has never priced: an inference with no
   * confidence to calibrate, a fact wrongly carrying a confidence (B2: a
   * fact's confidence is always null), or an `evidenceKind` string that
   * does not match any key in `EVIDENCE_WEIGHT`. Section 0: when unsure,
   * don't, so a malformed row is excluded rather than guessed into whichever
   * array looks closest, and counted here rather than silently dropped.
   */
  readonly excludedMalformed: number;
  /** Rows the recall-time screen quarantined (M1), oldest first. */
  readonly quarantined: readonly LeadCardQuarantined[];
}

function isEvidenceKind(kind: string): kind is EvidenceKind {
  return Object.prototype.hasOwnProperty.call(EVIDENCE_WEIGHT, kind);
}

function ageDays(writtenAt: string, now: Date): number {
  const written = Date.parse(writtenAt);
  if (Number.isNaN(written)) return 0;
  return Math.max(0, (now.getTime() - written) / DAY_MS);
}

/** Deterministic, never model-graded (section 4). Mirrors
 * `memorySummary.ts`'s `weightOf` * decay, duplicated per the module doc
 * comment above. */
function decayedWeight(kind: EvidenceKind, writtenAt: string, now: Date): number {
  return EVIDENCE_WEIGHT[kind] * Math.pow(0.5, ageDays(writtenAt, now) / LEAD_CARD_MEMORY_HALF_LIFE_DAYS);
}

interface Weighted {
  readonly weight: number;
  readonly writtenAt: string;
  readonly memoryId: string;
}

/** Highest weight first; a tie breaks on recency, then on the memory row's
 * own id, so two renders of the same card never disagree about order
 * (mirrors `outcomeDigest.ts`'s tie-break discipline). */
function byWeightThenRecencyThenId(a: Weighted, b: Weighted): number {
  if (b.weight !== a.weight) return b.weight - a.weight;
  const byDate = Date.parse(b.writtenAt) - Date.parse(a.writtenAt);
  if (byDate !== 0) return byDate;
  return a.memoryId.localeCompare(b.memoryId);
}

/** Most recent first, then the memory row's own id. No weight is computed
 * for a held row (see `LeadCardHeldContradiction`), so recency is the only
 * ordering this file will assert about unresolved knowledge. */
function byRecencyThenId(
  a: LeadCardHeldContradiction,
  b: LeadCardHeldContradiction,
): number {
  const byDate = Date.parse(b.writtenAt) - Date.parse(a.writtenAt);
  if (byDate !== 0) return byDate;
  return a.memoryId.localeCompare(b.memoryId);
}

/**
 * Assembles a `LeadCard` from whatever memory rows a caller hands it.
 *
 * Deliberately not a cap: this does not apply `memorySummary.ts`'s
 * `MEMORY_SUMMARY_MAX` (12), because that cap exists to fit a prompt's token
 * budget, and a lead card is not prompt real estate. Every `active` or
 * `held` row handed in is considered; the store's own ~40-per-contact
 * write-side cap (B2) is what bounds this in practice, and if that cap is
 * ever exceeded, `totalRows` says so honestly rather than a second,
 * unannounced read-side cap silently truncating the picture (FINDINGS.md
 * TA-186).
 */
export function buildLeadCard(input: BuildLeadCardInput): LeadCard {
  const facts: LeadCardFact[] = [];
  const inferences: LeadCardInference[] = [];
  const heldContradictions: LeadCardHeldContradiction[] = [];
  let excludedRetired = 0;
  let excludedMalformed = 0;

  // M1: the rows this card would show, screened again as one text. Retired
  // rows are out of the combination for the reason they are out of the card.
  const shown = input.memory.filter((r) => r.status !== "superseded" && r.status !== "pruned");
  const recall = screenRecalledMemory(shown, input.screen);
  const quarantinedRows = new Set(recall.quarantined);
  const quarantined: LeadCardQuarantined[] = recall.quarantined
    .map((r) => ({ memoryId: r.memoryId, evidenceKind: r.evidenceKind, writtenAt: r.writtenAt, categories: recall.categories }))
    .sort((a, b) => Date.parse(a.writtenAt) - Date.parse(b.writtenAt) || a.memoryId.localeCompare(b.memoryId));

  for (const row of input.memory) {
    // Retired first, regardless of how well-formed the rest of the row is:
    // a superseded or pruned row is out of scope for what the shop
    // currently knows, and its evidence kind's validity is not this card's
    // concern any more.
    if (row.status === "superseded" || row.status === "pruned") {
      excludedRetired++;
      continue;
    }

    // Counted in `quarantined`, and on no list a reader could act on.
    if (quarantinedRows.has(row)) continue;

    if (!isEvidenceKind(row.evidenceKind)) {
      excludedMalformed++;
      continue;
    }
    const evidenceKind = row.evidenceKind;

    if (row.status === "held") {
      heldContradictions.push({
        content: row.content,
        evidenceKind,
        isInference: row.isInference,
        writtenAt: row.writtenAt,
        saveReason: row.saveReason,
        memoryId: row.memoryId,
      });
      continue;
    }

    // Enforced, not asserted (core-duplication/memory-status-union-declared-twice):
    // the other three statuses are handled above, so this must narrow to
    // "active". A fifth `MemoryStatus` value fails to compile here rather
    // than falling silently into the active branch.
    row.status satisfies "active";
    if (row.isInference) {
      // An inference with nothing to calibrate is not "confidence unknown
      // treated as zero" here: `memorySummary.ts` makes that choice for
      // prompt ranking, where a bottom-ranked line simply never renders.
      // A lead card is an audit artifact, not a ranked-and-truncated list,
      // so silently ranking it last would still show it; excluding it and
      // counting it is the honest option (section 0).
      if (row.confidence === null) {
        excludedMalformed++;
        continue;
      }
      inferences.push({
        content: row.content,
        evidenceKind,
        confidence: row.confidence,
        weight: decayedWeight(evidenceKind, row.writtenAt, input.now) * row.confidence,
        writtenAt: row.writtenAt,
        sourceQuote: row.sourceQuote,
        saveReason: row.saveReason,
        memoryId: row.memoryId,
      });
    } else {
      // B2: a fact's confidence is always null. A fact row carrying a
      // confidence anyway blurs exactly the line this whole file exists to
      // hold apart, so it is excluded rather than silently accepted with
      // its stray confidence dropped on the floor.
      if (row.confidence !== null) {
        excludedMalformed++;
        continue;
      }
      facts.push({
        content: row.content,
        evidenceKind,
        weight: decayedWeight(evidenceKind, row.writtenAt, input.now),
        writtenAt: row.writtenAt,
        sourceQuote: row.sourceQuote,
        saveReason: row.saveReason,
        memoryId: row.memoryId,
      });
    }
  }

  facts.sort(byWeightThenRecencyThenId);
  inferences.sort(byWeightThenRecencyThenId);
  heldContradictions.sort(byRecencyThenId);

  return {
    contactId: input.contactId,
    generatedAt: input.now.toISOString(),
    facts,
    inferences,
    heldContradictions,
    totalRows: input.memory.length,
    excludedRetired,
    excludedMalformed,
    quarantined,
  };
}
